import AssessmentIcon from '@mui/icons-material/Assessment';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import SportsScoreIcon from '@mui/icons-material/SportsScore';
import { Box, Button, Typography, useMediaQuery } from '@mui/material';
import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { BoxCard } from '../../../../core/BoxCard';
import {
  extractCourseCategory,
  extractGainMax,
  extractReculValue,
  formatCategorieParticularite,
  formatDiscipline,
  formatHippodromeName
} from '../../../../utils/format';
import { StatusCourse, Terrain, TypeCourse } from '../../services/enum';
import { Course, Reunion } from '../../services/type';
import React from 'react';
import SectionTitle from '../../../../core/SectionTitle';
import NumberCircle from '../../../../core/NumberCircle';


interface Props {
  course: Course;
  reunion: Reunion;
}

const HeaderProno: FC<Props> = ({ reunion, course }) => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const categorie = extractCourseCategory(course.conditions);
  const plafond = extractGainMax(course.conditions);
  const recul = extractReculValue(course.conditions, plafond);
  const typePiste = formatCategorieParticularite(
    course.penetrometre?.intitule.includes('PSF')
      ? Terrain.PSF_STANDARD
      : course.typePiste
  );

  const handleClickStat = () => {
    navigate("stats")
  }

  const handleBack = () => {
    navigate(-1)
  }

  return (
    <Box minHeight={'50px'}>
      <Box display="flex" alignItems="center" gap={1} width="100%">
        {/* BLOC R / C */}
        <Box
          bgcolor="primary.main"
          width={isMobile ? '75px' : '90px'}
          height="70px"
          display="flex"
          borderRadius={2}
          justifyContent="flex-start"
          pl={isMobile ? 1 : 2}
          color="white"
          alignItems="center"
          sx={{
            borderTopRightRadius: 0,
            clipPath: 'polygon(0% 0%, 100% 0%, 80% 100%, 0% 100%)',
          }}
        >
          <Typography fontWeight={700} fontSize={20}>
            {`R${reunion.numOfficiel} C${course.numOrdre}`}
          </Typography>
        </Box>

        {/* TITRES + STATS */}
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="flex-start"
          maxWidth={isMobile ? '230px' : '420px'}
        >
          {/* LIGNE 1 : HIPPO + STATS */}
          <Box display="flex" alignItems="flex-start" gap={0.5}>
            <Typography
              variant={isMobile ? 'subtitle1' : 'h5'}
              color="primary"
              fontWeight={600}
              sx={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {formatHippodromeName(reunion.hippodrome.libelleLong)}
            </Typography>

            {/* BOUTON STATS */}
            <Button
              variant="text"
              color="secondary"
              onClick={handleClickStat}
              sx={{
                minWidth: 'auto',
                px: 0.5,
                py: 0,
              }}
            >
              <AssessmentIcon sx={{ fontSize: 26 }} />
            </Button>
          </Box>

          {/* LIGNE 2 : COURSE */}
          <Box>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              fontWeight={500}
              alignSelf={"flex-start"}
              sx={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {course.libelle}
            </Typography>
          </Box>
        </Box>

        {/* SPACER */}
        <Box flex={1} />

        {/* BOUTON RETOUR */}
        {!isMobile && <Box
          onClick={handleBack}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            cursor: 'pointer',
            px: 1.5,
            py: 0.75,
            borderRadius: 2,
            border: '1px solid rgba(255,255,255,0.6)',
            fontSize: 13,
            transition: 'all 0.2s ease',
            '&:hover': {
              bgcolor: 'rgba(255,255,255,0.1)',
              borderColor: 'white',
            },
          }}
        >
          <ChevronLeftIcon sx={{ fontSize: 18 }} />
          <Typography variant="caption" fontWeight={600}>
            Retour
          </Typography>
        </Box>
        }
      </Box>


      <Box
        display={'flex'}
        flexDirection={'row'}
        alignItems={'baseline'}
        gap={isMobile ? 0.5 : 1}
        flexWrap="wrap"
        color={'primary.main'}
        mt={1}
        mr={2}
      >
        <BoxCard small>
          <Typography color="white" variant="body1">
            {formatDiscipline(course.discipline, isMobile)}
          </Typography>
        </BoxCard>
        <BoxCard small>
          <Typography color="white" variant="body1">
            {`${course.distance}m`}
          </Typography>
        </BoxCard>
        <BoxCard small>
          <Typography
            color="white"
            variant="body1"
          >{`${course.nombreDeclaresPartants} partants`}</Typography>
        </BoxCard>
        <BoxCard small>
          <Typography
            color="white"
            variant="body1"
          >{`${course.montantPrix.toLocaleString('fr-FR')}€`}</Typography>
        </BoxCard>
        {categorie && (
          <BoxCard small>
            <Typography
              color="white"
              variant="body1"
            >{`catégorie: ${categorie}`}</Typography>
          </BoxCard>
        )}
        {plafond && (
          <BoxCard small>
            <Typography
              color="white"
              fontSize={'0.9rem'}
              variant="body1"
            >{`plafond: ${plafond.toLocaleString('fr-FR')}€`}</Typography>
          </BoxCard>
        )}
        {(recul || 0) > 0 && (
          <BoxCard small>
            <Typography
              color="white"
              variant="body1"
            >{`recul: ${recul?.toLocaleString('fr-FR')}€`}</Typography>
          </BoxCard>
        )}
        {course.categorieParticularite !== TypeCourse.INCONNU && (
          <BoxCard small>
            <Typography color="white" variant="body1">
              {formatCategorieParticularite(
                course.categorieParticularite
              )}
            </Typography>
          </BoxCard>
        )}
        {typePiste && typePiste.length > 0 && (
          <BoxCard small>
            <Box display={'flex'} flexDirection={'row'} gap={0.5}>
              <Typography color="white" variant="body1">
                {typePiste}
              </Typography>
              {course.penetrometre?.valeurMesure && (
                <Typography
                  color="white"
                  variant="body1"
                >{`(${course.penetrometre?.valeurMesure})`}</Typography>
              )}
            </Box>
          </BoxCard>
        )}
      </Box>
      {course.categorieStatut === StatusCourse.ARRIVEE && (
        <Box mt={1.5} display={'flex'} flexDirection={'column'} gap={0.75}>
          <SectionTitle icon={SportsScoreIcon} label="Arrivée" />

          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="center"
            gap={0.75}
            flexWrap="wrap"
            p={0.5}
          >
            {course.ordreArrivee.slice(0, 5).map((numero, index) => (
              <React.Fragment key={numero}>
                <NumberCircle
                  numero={numero}
                  size={index === 0 ? 42 : 36}
                  variant={index === 0 ? 'gold' : 'default'}
                />
                {index < course.ordreArrivee.slice(0, 5).length - 1 && (
                  <Typography color="text.disabled" fontSize={13} fontWeight={300}>
                    →
                  </Typography>
                )}
              </React.Fragment>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default HeaderProno;
